import { Injectable } from '@angular/core';
import { ActionQuery } from '@ep-om/project/state/action/action.query';
import {
  acknowledgeRealtimeV2LocalMutation,
  getRealtimeV2EntityUpdateContext,
  getRealtimeV2LocalMutationSeq,
  RealtimeV2EntityUpdateContext,
} from './realtime-v2-update-context';

export interface FieldPatch {
  field: string;
  value: unknown;
}

/**
 * Riconciliazione per-campo dell'entita' in arrivo dal realtime contro l'input locale.
 *
 * Il core e' esposto come funzioni pure parametrizzate da ActionQuery: cosi' il pattern legacy
 * (AbstractInterfaceDataComponent), che NON puo' aggiungere dipendenze al costruttore senza
 * toccare i super(...) delle ~50 sottoclassi - e su Angular 13 non puo' usare inject() fuori
 * da una factory - le invoca passando il proprio this.actionQuery. Il nuovo pattern usa invece
 * EntityReconciliationService (DI normale via costruttore nei Presenter). Unica implementazione.
 *
 * Tre attori (vedi docs/architettura-feature-frontend.md):
 *  - Outbox marca/azzera le mutation locali (mutationSeq).
 *  - Sync registra l'update-context (origin, version, sameOrigin) all'arrivo.
 *  - Qui si decide, per-campo, se proteggere l'input locale o applicare il dato remoto.
 *
 * La protezione e' SOLO form-side: lo store resta verita' condivisa monotona (gate version).
 */

/**
 * Decisione legacy-compatibile: oltre a decidere, sul ramo "eco confermato" CHIUDE la mutation
 * locale (riapre il campo). Drop-in del vecchio shouldSkipPatchValue. NON pura: muta il registro.
 */
export function reconcileShouldSkip(
  actionQuery: ActionQuery,
  entityId: string,
  field: string,
  ctx?: RealtimeV2EntityUpdateContext,
): boolean {
  // Calcola la protezione PRIMA dell'ack: l'ack azzera il mutationSeq locale su cui si basa la
  // decisione. L'ack va sempre eseguito (anche quando si salta il patch) altrimenti la mutation
  // locale non si chiude mai e il campo resta protetto per sempre dagli update futuri.
  const skip = isFieldProtected(actionQuery, entityId, field, ctx);
  acknowledgeEcho(entityId, field, ctx);
  return skip;
}

/**
 * Nuovo pattern. Decisione PURA: non muta il registro, ritorna il piano di patch.
 * L'ack dell'eco va eseguito a parte con acknowledgeConfirmedEchoes (in un tap, non un map)
 * e SEMPRE DOPO questo compute: l'ack azzera il mutationSeq locale su cui si basa la protezione.
 * Itera tutte le chiavi dell'entity (inclusi version/updatedAt/campi server): la View deve
 * filtrare i patch sui control effettivamente esistenti nel FormGroup.
 */
export function computeEntityPatches(
  actionQuery: ActionQuery,
  entityId: string,
  entity: Record<string, unknown>,
  version?: number,
): FieldPatch[] {
  const ctx = getRealtimeV2EntityUpdateContext(entityId, version);
  return Object.keys(entity)
    .filter((field) => field !== 'id')
    .filter((field) => !isFieldProtected(actionQuery, entityId, field, ctx))
    .map((field) => ({ field, value: entity[field] }));
}

/**
 * Nuovo pattern. Side-effect: chiude le mutation locali confermate dall'eco del proprio
 * salvataggio. Da invocare in un tap A VALLE di computeEntityPatches (dopo che il piano di patch
 * e' gia' stato calcolato), altrimenti azzera il mutationSeq prima della decisione di protezione.
 */
export function acknowledgeConfirmedEchoes(
  entityId: string,
  entity: Record<string, unknown>,
  version?: number,
): void {
  const ctx = getRealtimeV2EntityUpdateContext(entityId, version);
  for (const field of Object.keys(entity)) {
    if (field === 'id') {
      continue;
    }
    acknowledgeEcho(entityId, field, ctx);
  }
}

/**
 * Store-side counterpart della protezione form. Le viste senza form (board kanban, colonne di
 * tabella, card) leggono i campi direttamente dallo store: l'eco dei MIEI cambi intermedi arriva
 * con version piu' alta di quella (mai avanzata in locale dall'update ottimistico) dello store,
 * quindi il version-gate lo lascia passare e regredisce il valore (es. stateId S3 -> S1 -> S2 -> S3).
 *
 * Qui, per il SOLO self-echo, conserviamo il valore corrente dei campi che ho gia' sovrascritto
 * con una mutation locale STRETTAMENTE piu' recente (localSeq > origin.mutationSeq): l'eco confermante
 * (localSeq === originSeq) va invece applicato e poi chiuso dall'ack. Gli update cross-origin (altri
 * utenti) restano invariati: lo store resta verita' condivisa monotona.
 *
 * PURA: legge il registro ma non lo muta. Ritorna l'entita' in arrivo con i campi superati rimpiazzati
 * dal valore corrente; se non c'e' nulla da proteggere ritorna il riferimento in arrivo invariato.
 *
 * NB: l'ack del registro NON avviene qui ne' a livello store. Resta di competenza del form
 * (reconcileShouldSkip), che con la soglia >= protegge anche i tasti non ancora salvati nel gap del
 * debounce: chiuderlo in anticipo lato store scoprirebbe quei tasti sull'eco confermante. Per i
 * cambi solo-store (kanban) la entry non viene chiusa, ma e' innocua: gli echi same-origin hanno
 * sempre mutationSeq <= localSeq (solo i piu' vecchi restano protetti, correttamente) e i cambi
 * cross-origin bypassano la protezione; una nuova modifica sullo stesso campo la sovrascrive.
 */
export function reconcileIncomingStoreEntity(
  entityId: string,
  incoming: Record<string, unknown>,
  current: Record<string, unknown> | undefined,
  ctx?: RealtimeV2EntityUpdateContext,
): Record<string, unknown> {
  const originMutationSeq = ctx?.origin?.mutationSeq;
  if (!current || !ctx?.sameOrigin || originMutationSeq === undefined) {
    return incoming;
  }

  let merged: Record<string, unknown> | undefined;
  for (const field of Object.keys(incoming)) {
    if (field === 'id') {
      continue;
    }

    const localSeq = getRealtimeV2LocalMutationSeq(entityId, field);
    if (localSeq !== undefined && localSeq > originMutationSeq && field in current) {
      merged = merged ?? { ...incoming };
      merged[field] = current[field];
    }
  }

  return merged ?? incoming;
}

/**
 * True se il campo non va patchato: edit di un altro utente su campo che ho in volo,
 * oppure eco superato da un mio input piu' recente (mutationSeq). PURA.
 */
function isFieldProtected(
  actionQuery: ActionQuery,
  entityId: string,
  field: string,
  ctx?: RealtimeV2EntityUpdateContext,
): boolean {
  if (!ctx || !ctx.origin || !ctx.sameOrigin) {
    return actionQuery.isItemAndKeyChanging(entityId, field);
  }

  const localMutationSeq = getRealtimeV2LocalMutationSeq(entityId, field);
  const originMutationSeq = ctx.origin.mutationSeq;
  if (originMutationSeq === undefined) {
    return localMutationSeq !== undefined || actionQuery.isItemAndKeyChanging(entityId, field);
  }

  // Ho scritto qualcosa di recente almeno quanto l'eco -> proteggo il mio input.
  // >= (non >): l'eco del MIO ultimo salvataggio ha localSeq == originSeq, ma tra il salvataggio
  // e il ritorno del backend l'utente puo' aver continuato a digitare (gap del debounce) senza che
  // sia ancora partita una nuova mutation. Applicare l'eco sovrascriverebbe i tasti non ancora salvati.
  return localMutationSeq !== undefined && localMutationSeq >= originMutationSeq;
}

/**
 * Chiude la mutation locale quando l'eco proviene dal mio socket ed e' recente almeno
 * quanto la mia mutazione. Side-effect sul registro.
 */
function acknowledgeEcho(entityId: string, field: string, ctx?: RealtimeV2EntityUpdateContext): void {
  if (!ctx?.origin || !ctx.sameOrigin) {
    return;
  }

  const originMutationSeq = ctx.origin.mutationSeq;
  if (originMutationSeq === undefined) {
    return;
  }

  acknowledgeRealtimeV2LocalMutation(entityId, field, originMutationSeq);
}

/**
 * Wrapper DI per il nuovo pattern Presenter-based. I Presenter iniettano questo servizio via
 * costruttore (DI normale). Delega al core condiviso sopra: unica implementazione della logica.
 */
@Injectable({ providedIn: 'root' })
export class EntityReconciliationService {
  constructor(private actionQuery: ActionQuery) {}

  shouldSkip(entityId: string, field: string, ctx?: RealtimeV2EntityUpdateContext): boolean {
    return reconcileShouldSkip(this.actionQuery, entityId, field, ctx);
  }

  computePatches(entityId: string, entity: Record<string, unknown>, version?: number): FieldPatch[] {
    return computeEntityPatches(this.actionQuery, entityId, entity, version);
  }

  acknowledgeConfirmedEchoes(entityId: string, entity: Record<string, unknown>, version?: number): void {
    acknowledgeConfirmedEchoes(entityId, entity, version);
  }
}
